import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

/**
 * Custom hook for property analysis workflow
 * Uses backend API for full analysis including AI insights
 * @returns {Object} - { analyze, loading, error, result }
 */
export function usePropertyAnalysis() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  /**
   * Analyze a property from URL using backend API
   * @param {string} url - Property URL
   */
  const analyze = useCallback(async (url) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {

      // Call the backend analyze-url endpoint
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/properties/analyze-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }

      const responseData = await response.json();

      if (!responseData.success) {
        throw new Error(responseData.message || 'Analysis failed');
      }

      const { propertyData, analysis, analyzedAt, processingTime } = responseData.data;

      // Set the complete result
      const finalResult = {
        propertyData,
        analysis,
        url,
        analyzedAt,
        processingTime
      };

      setResult(finalResult);
    } catch (err) {
      // Check if it's a network error (backend not running)
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError(`Cannot connect to server. Please ensure the backend is running and accessible at ${API_BASE_URL}`);
      } else {
        setError(err.message || 'An unexpected error occurred while analyzing the property');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  /**
   * Reset the analysis state
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setResult(null);
  }, []);

  /**
   * Clear only the error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    analyze,
    reset,
    clearError,
    loading,
    error,
    result
  };
}
